import React, { useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { useSnackbar } from '../contexts/SnackbarContext';
import { type StreamStats } from '../services/streamsService';
import { type StreamChannel } from '../services/channelsService';

interface Props {
  stats: StreamStats | null;
  channel?: Pick<StreamChannel, 'name' | 'streamKeyPin'> | null;
}

export const StreamDetailsCard: React.FC<Props> = ({ stats, channel }) => {
  const [protocol, setProtocol] = useState<'srt' | 'rtmp'>('srt');
  const { showSnackbar } = useSnackbar();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied!', 'success');
  };

  if (!stats && !channel) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 0, p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <VpnKeyIcon sx={{ fontSize: '1.25rem', color: 'secondary.main' }} />
          <Typography variant="h6" fontWeight={900}>Stream Details</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Stream details will appear here once your stream is live.
        </Alert>
      </Paper>
    );
  }

  const streamName = channel ? channel.name : stats?.streamName || '';
  const streamKeyPin = channel ? channel.streamKeyPin || '' : stats?.streamKeyPin || '';
  const fullStreamKey = `${streamName}${streamKeyPin}`;
  const streamPublicHost = window.location.hostname;
  const rtmpUrl = `rtmp://${streamPublicHost}/live/${fullStreamKey}`;
  const rtmpServerUrl = `rtmp://${streamPublicHost}/live/`;
  const srtStreamId = `publish:live/${fullStreamKey}`;
  const srtUrl = `srt://${streamPublicHost}:8890?streamid=${srtStreamId}`;
  const srtDomain = streamPublicHost;
  const srtPort = '8890';

  const copyAdornment = (value: string, label: string, disabled = false) => (
    <InputAdornment position="end">
      <Tooltip title={`Copy ${label}`}>
        <span>
          <IconButton onClick={() => copyToClipboard(value)} edge="end" disabled={disabled}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );

  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 0 } };

  return (
    <Paper elevation={0} sx={{ borderRadius: 0, p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <VpnKeyIcon sx={{ fontSize: '1.25rem', color: 'secondary.main' }} />
        <Typography variant="h6" fontWeight={900}>Stream Details</Typography>
      </Stack>
      <Divider sx={{ mb: 1.5 }} />

      <FormControl component="fieldset">
        <RadioGroup row value={protocol} onChange={(event) => setProtocol(event.target.value as 'srt' | 'rtmp')}>
          <FormControlLabel value="srt" control={<Radio size="small" />} label="SRT" />
          <FormControlLabel value="rtmp" control={<Radio size="small" />} label="RTMP" />
        </RadioGroup>
      </FormControl>

      {protocol === 'srt' && (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <TextField label="SRT URL" value={srtUrl} helperText="Complete URL for encoders with one server field." fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(srtUrl, 'SRT URL', !streamKeyPin) }} sx={fieldSx} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(100px, 1fr)' }, gap: 1.5 }}>
            <TextField label="SRT Domain" value={srtDomain} helperText="Server domain" fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(srtDomain, 'domain') }} sx={fieldSx} />
            <TextField label="SRT Port" value={srtPort} helperText="Port" fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(srtPort, 'port') }} sx={fieldSx} />
          </Box>
          <TextField label="SRT Stream ID" value={srtStreamId} helperText="For encoders with a separate Stream ID field." fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(srtStreamId, 'Stream ID', !streamKeyPin) }} sx={fieldSx} />
        </Stack>
      )}

      {protocol === 'rtmp' && (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <TextField label="RTMP URL" value={rtmpUrl} helperText="Complete URL for encoders with one server field." fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(rtmpUrl, 'RTMP URL', !streamKeyPin) }} sx={fieldSx} />
          <TextField label="RTMP Server URL" value={rtmpServerUrl} helperText="Server URL without stream key." fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(rtmpServerUrl, 'server URL') }} sx={fieldSx} />
          <TextField label="Stream Key" value={fullStreamKey} helperText="For encoders with a separate Stream Key field." fullWidth size="small" InputProps={{ readOnly: true, endAdornment: copyAdornment(fullStreamKey, 'Stream Key', !streamKeyPin) }} sx={fieldSx} />
        </Stack>
      )}
    </Paper>
  );
};
